// EXPERIMENTAL — Proposal Sandbox / Concept Preview
// Isolated from production Proposal flow. Safe to delete:
// only referenced by the /proposal-sandbox route in App.js.

import React, { useState } from "react";
import {
  ShieldCheck,
  Clock,
  Users,
  ClipboardCheck,
  Handshake,
  Check,
  Printer,
  Monitor,
  FileText,
  FolderOpen,
  Phone,
  Mail,
  Globe,
  MapPin,
} from "lucide-react";

/* ──────────────────────────────────────────────────────────────
   DUMMY DATA
   ────────────────────────────────────────────────────────────── */
const DEMO = {
  client: "Carolina Infrastructure Group",
  clientAddr: ["810 Trade St. NW, Suite 300", "Charlotte, NC 28202"],
  clientAttn: "Project Manager",
  project: "South End Development – Phase 2",
  projectCity: "Charlotte, NC",
  proposalNumber: "CLL-250430-01",
  issueDate: "April 30, 2025",
  validFor: "30 Days",
  scope: [
    "Utility Designation (GPR & EM)",
    "Utility Potholing (Hydro Excavation)",
    "CCTV Inspection (Storm / Sanitary / Conduit)",
    "Data Processing & CAD Deliverables",
    "Project Management & Communication",
  ],
  investment: [
    { label: "Utility Designation", amount: 8450 },
    { label: "Potholing", amount: 12750 },
    { label: "CCTV Inspection", amount: 6250 },
    { label: "Deliverables & Reporting", amount: 2350 },
    { label: "Project Management", amount: 3200 },
  ],
  commitment: [
    { icon: ShieldCheck, title: "Reduce Risk", line1: "Avoid costly hits", line2: "and liabilities." },
    { icon: Clock, title: "Prevent Delays", line1: "Keep projects", line2: "on schedule." },
    { icon: Users, title: "One Partner", line1: "All services.", line2: "One point of contact." },
    { icon: ClipboardCheck, title: "Accurate Info", line1: "Correct the unknowns.", line2: "Verify everything." },
    { icon: Handshake, title: "Everyone Wins", line1: "Successful projects.", line2: "Strong relationships." },
  ],
  signer: {
    name: "Zach Henderson",
    title: "Director of Operations",
    company: "Center Line Locating",
  },
  contact: {
    phone: "704.804.2448",
    email: "info@centerlinelocating.com",
    web: "centerlinelocating.com",
    city: "Charlotte, North Carolina",
  },
  // Detailed scope retained for the cinematic Screen view
  scopeDetail: [
    { title: "Utility Designation", detail: "Locate, mark, and document all detectable subsurface utilities within the project limits." },
    { title: "Ground Penetrating Radar", detail: "Multi-frequency GPR sweep for non-conductive lines, voids, and unknown obstructions." },
    { title: "Hydro / Vacuum Excavation", detail: "Non-destructive potholing to verify depth, alignment, and material of critical crossings." },
    { title: "CCTV Inspection", detail: "Internal pipe survey with high-resolution video, defect log, and routing report." },
    { title: "Survey Support & CAD", detail: "Coordinate-grade pickups, deliverable-ready CAD overlays, and GIS exports." },
  ],
};

const total = DEMO.investment.reduce((s, i) => s + i.amount, 0);
const fmt = (n) =>
  n.toLocaleString("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2 });

const GOLD = "#B89B67";
const GOLD_DEEP = "#8C6E3A";
const INK = "#111111";

/* ──────────────────────────────────────────────────────────────
   ICONOGRAPHY
   ────────────────────────────────────────────────────────────── */

// Faux CLL "crosshair" wordmark — visual approximation for the sandbox.
// Real proposals will swap in /logo.png unchanged.
const CllWordmark = ({ height = 44, dark = false }) => {
  const ink = dark ? "#fff" : "#1a1a1a";
  return (
    <svg viewBox="0 0 780 110" height={height} style={{ width: "auto", maxWidth: "100%" }} data-testid="cll-wordmark">
      {/* top dashes */}
      <g stroke="none">
        <rect x="155" y="2" width="4" height="16" fill="#D6443A" />
        <rect x="310" y="2" width="4" height="16" fill="#3B6FB6" />
        <rect x="460" y="2" width="4" height="16" fill="#E07A2C" />
        <rect x="610" y="2" width="4" height="16" fill="#3FA86A" />
      </g>
      {/* wordmark */}
      <text
        x="0"
        y="68"
        fontFamily="Impact, 'Arial Black', 'Helvetica Neue', sans-serif"
        fontWeight="900"
        fontSize="56"
        letterSpacing="2"
        fill={ink}
      >
        CENTER LINE LOCATING
      </text>
      {/* bottom dashes */}
      <g stroke="none">
        <rect x="155" y="86" width="4" height="16" fill="#D6443A" />
        <rect x="310" y="86" width="4" height="16" fill="#3B6FB6" />
        <rect x="460" y="86" width="4" height="16" fill="#E07A2C" />
        <rect x="610" y="86" width="4" height="16" fill="#3FA86A" />
      </g>
    </svg>
  );
};

// Hexagonal CLL monogram (matches the seal center & sign-off corner)
const CllHexMark = ({ size = 60, color = GOLD, stroke = 2 }) => (
  <svg viewBox="0 0 80 88" width={size} height={(size * 88) / 80}>
    <path
      d="M40 3 L74 22 L74 66 L40 85 L6 66 L6 22 Z"
      fill="none"
      stroke={color}
      strokeWidth={stroke}
    />
    <text
      x="40"
      y="55"
      textAnchor="middle"
      fontFamily="Georgia, serif"
      fontWeight="700"
      fontSize="28"
      letterSpacing="-1"
      fill={color}
    >
      CLL
    </text>
  </svg>
);

// Embossed gold seal — matches the mockup
const EmbossedSeal = ({ size = 180 }) => (
  <svg viewBox="0 0 220 220" width={size} height={size} data-testid="embossed-seal">
    <defs>
      <radialGradient id="sealFill" cx="0.35" cy="0.3" r="0.85">
        <stop offset="0%" stopColor="#F0D89C" />
        <stop offset="45%" stopColor="#B89B67" />
        <stop offset="100%" stopColor="#6B5024" />
      </radialGradient>
      <linearGradient id="sealRing" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor="#E7CD8E" />
        <stop offset="100%" stopColor="#7E6736" />
      </linearGradient>
      <path
        id="sealText"
        d="M 110,110 m -78,0 a 78,78 0 1,1 156,0 a 78,78 0 1,1 -156,0"
      />
    </defs>
    {/* Scalloped outer edge */}
    <g transform="translate(110 110)">
      {Array.from({ length: 36 }).map((_, i) => {
        const angle = (i * 360) / 36;
        return (
          <circle
            key={i}
            cx="0"
            cy="-100"
            r="6"
            fill="url(#sealFill)"
            transform={`rotate(${angle})`}
          />
        );
      })}
    </g>
    <circle cx="110" cy="110" r="95" fill="url(#sealFill)" />
    <circle cx="110" cy="110" r="88" fill="none" stroke="url(#sealRing)" strokeWidth="1.5" />
    <circle cx="110" cy="110" r="62" fill="none" stroke="#6B5024" strokeWidth="0.8" opacity="0.6" />
    {/* Curved text */}
    <text fontSize="11" letterSpacing="3" fill="#3D2B0F" fontFamily="Georgia, serif" fontWeight="700">
      <textPath href="#sealText" startOffset="6%">
        · PROJECT COVERED ·
      </textPath>
      <textPath href="#sealText" startOffset="56%">
        · CONFIDENCE DELIVERED ·
      </textPath>
    </text>
    {/* tiny star separators */}
    <g fill="#3D2B0F">
      <circle cx="110" cy="40" r="1.6" />
      <circle cx="110" cy="180" r="1.6" />
    </g>
    {/* Center hex monogram */}
    <g transform="translate(72 60)">
      <path
        d="M38 3 L70 20 L70 62 L38 79 L6 62 L6 20 Z"
        fill="none"
        stroke="#3D2B0F"
        strokeWidth="2"
      />
      <text
        x="38"
        y="51"
        textAnchor="middle"
        fontFamily="Georgia, serif"
        fontWeight="700"
        fontSize="26"
        letterSpacing="-1"
        fill="#3D2B0F"
      >
        CLL
      </text>
    </g>
  </svg>
);

/* ──────────────────────────────────────────────────────────────
   SINGLE-PAGE PROPOSAL (matches mockup) — used in Folder + Print
   ────────────────────────────────────────────────────────────── */
const SinglePageProposal = ({ scale = 1, asPrintPage = false }) => {
  const wrapperStyle = asPrintPage
    ? {
        width: "8.5in",
        minHeight: "11in",
        padding: "0.55in 0.6in 0.5in",
        background: "#fff",
        color: INK,
        margin: "0 auto",
        boxShadow: "0 8px 28px rgba(0,0,0,0.18)",
        fontFamily: '"Inter", system-ui, sans-serif',
        fontSize: "9.5pt",
        position: "relative",
      }
    : {
        width: 660 * scale,
        minHeight: 854 * scale,
        padding: `${44 * scale}px ${48 * scale}px ${40 * scale}px`,
        background: "#fff",
        color: INK,
        fontFamily: '"Inter", system-ui, sans-serif',
        fontSize: 11 * scale,
        position: "relative",
        boxShadow: "0 18px 60px rgba(0,0,0,0.45)",
      };

  return (
    <div style={wrapperStyle} data-testid="single-page-proposal">
      {/* HEADER */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div>
          <CllWordmark height={asPrintPage ? 40 : 36 * scale} />
          <div
            style={{
              marginTop: 8,
              fontSize: asPrintPage ? "7.5pt" : 9 * scale,
              letterSpacing: "0.35em",
              color: GOLD_DEEP,
              fontWeight: 600,
            }}
          >
            PRECISION. COMMUNICATION. DELIVERY.
          </div>
        </div>
        <div style={{ textAlign: "right", minWidth: asPrintPage ? "2.4in" : 200 * scale }}>
          <div
            style={{
              fontFamily: "Georgia, serif",
              fontSize: asPrintPage ? "18pt" : 22 * scale,
              fontWeight: 700,
              letterSpacing: "0.06em",
              color: INK,
            }}
          >
            PROPOSAL
          </div>
          <table style={{ marginTop: 10, fontSize: asPrintPage ? "8.5pt" : 10 * scale, width: "100%" }}>
            <tbody>
              {[
                ["PROPOSAL NO.", DEMO.proposalNumber],
                ["DATE", DEMO.issueDate],
                ["VALID FOR", DEMO.validFor],
              ].map(([k, v]) => (
                <tr key={k}>
                  <td style={{ color: "#666", textTransform: "uppercase", letterSpacing: "0.05em", padding: "3px 0", fontWeight: 500 }}>
                    {k}
                  </td>
                  <td style={{ textAlign: "right", padding: "3px 0", color: INK }}>{v}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div style={{ height: 1, background: "#e6e6e6", margin: asPrintPage ? "18px 0 16px" : `${22 * scale}px 0 ${18 * scale}px` }} />

      {/* PREPARED FOR + PROJECT */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: asPrintPage ? "0.4in" : 30 * scale }}>
        <div>
          <div style={{ fontSize: asPrintPage ? "7.5pt" : 9 * scale, letterSpacing: "0.3em", color: GOLD_DEEP, fontWeight: 600 }}>
            PREPARED FOR
          </div>
          <div style={{ marginTop: 8, fontSize: asPrintPage ? "11pt" : 13 * scale, fontWeight: 600 }}>{DEMO.client}</div>
          {DEMO.clientAddr.map((l) => (
            <div key={l} style={{ fontSize: asPrintPage ? "9pt" : 10.5 * scale, color: "#444", marginTop: 2 }}>
              {l}
            </div>
          ))}
          <div style={{ marginTop: 10, fontSize: asPrintPage ? "9pt" : 10.5 * scale, color: "#444" }}>
            Attn: <span style={{ color: INK, fontWeight: 500 }}>{DEMO.clientAttn}</span>
          </div>
        </div>
        <div>
          <div style={{ fontSize: asPrintPage ? "7.5pt" : 9 * scale, letterSpacing: "0.3em", color: GOLD_DEEP, fontWeight: 600 }}>
            PROJECT
          </div>
          <div style={{ marginTop: 8, fontSize: asPrintPage ? "11pt" : 13 * scale, fontWeight: 600 }}>{DEMO.project}</div>
          <div style={{ fontSize: asPrintPage ? "9pt" : 10.5 * scale, color: "#444", marginTop: 2 }}>
            {DEMO.projectCity}
          </div>
        </div>
      </div>

      {/* THANK YOU + SEAL */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 16, marginTop: asPrintPage ? 22 : 24 * scale, alignItems: "center" }}>
        <p style={{ fontSize: asPrintPage ? "10pt" : 11.5 * scale, lineHeight: 1.65, color: "#333", fontWeight: 300, maxWidth: asPrintPage ? "4.6in" : 420 * scale }}>
          Thank you for the opportunity to provide this proposal. Center Line Locating is committed to
          delivering accurate data, clear communication, and on-time results to keep your project safe, on
          schedule, and moving forward.
        </p>
        <div style={{ paddingRight: asPrintPage ? 4 : 6 * scale }}>
          <EmbossedSeal size={asPrintPage ? 150 : 140 * scale} />
        </div>
      </div>

      <div style={{ height: 1, background: "#e6e6e6", margin: asPrintPage ? "20px 0 18px" : `${24 * scale}px 0 ${20 * scale}px` }} />

      {/* SCOPE + INVESTMENT */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: asPrintPage ? "0.45in" : 36 * scale }}>
        <div>
          <div style={{ fontSize: asPrintPage ? "9pt" : 11 * scale, letterSpacing: "0.22em", fontWeight: 700, color: INK }}>
            SCOPE OF WORK
          </div>
          <div style={{ marginTop: 14 }}>
            {DEMO.scope.map((s) => (
              <div key={s} style={{ display: "flex", alignItems: "center", gap: 12, padding: "8px 0", borderBottom: "1px solid #f0f0f0" }}>
                <span style={{ width: asPrintPage ? 16 : 18 * scale, height: asPrintPage ? 16 : 18 * scale, borderRadius: 999, border: `1.5px solid ${GOLD}`, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <Check size={asPrintPage ? 10 : 11 * scale} color={GOLD} strokeWidth={3} />
                </span>
                <span style={{ fontSize: asPrintPage ? "9.5pt" : 11 * scale, color: "#222", fontWeight: 400 }}>{s}</span>
              </div>
            ))}
          </div>
        </div>

        <div>
          <div style={{ fontSize: asPrintPage ? "9pt" : 11 * scale, letterSpacing: "0.22em", fontWeight: 700, color: INK }}>
            INVESTMENT SUMMARY
          </div>
          <table style={{ width: "100%", marginTop: 14, borderCollapse: "collapse" }}>
            <tbody>
              {DEMO.investment.map((row) => (
                <tr key={row.label} style={{ borderBottom: "1px solid #f0f0f0" }}>
                  <td style={{ padding: "9px 0", fontSize: asPrintPage ? "9.5pt" : 11 * scale, color: "#222" }}>{row.label}</td>
                  <td style={{ padding: "9px 0", fontSize: asPrintPage ? "9.5pt" : 11 * scale, textAlign: "right", fontVariantNumeric: "tabular-nums", color: "#222" }}>
                    {fmt(row.amount)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div style={{ fontSize: asPrintPage ? "7.5pt" : 9 * scale, color: "#888", fontStyle: "italic", marginTop: 8 }}>
            * Full scope of work and exclusions included in attached terms.
          </div>

          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginTop: 16, paddingTop: 12, borderTop: `2px solid ${GOLD}` }}>
            <span style={{ fontSize: asPrintPage ? "10pt" : 12 * scale, letterSpacing: "0.18em", fontWeight: 700, color: INK }}>
              TOTAL INVESTMENT
            </span>
            <span style={{ fontSize: asPrintPage ? "16pt" : 20 * scale, fontWeight: 700, color: GOLD_DEEP, fontVariantNumeric: "tabular-nums" }}>
              {fmt(total)}
            </span>
          </div>
        </div>
      </div>

      {/* THE CLL COMMITMENT */}
      <div style={{ marginTop: asPrintPage ? 26 : 32 * scale }}>
        <div style={{ position: "relative", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 18 }}>
          <div style={{ position: "absolute", left: 0, right: 0, height: 1, background: "#e6e6e6" }} />
          <div style={{ position: "relative", background: "#fff", padding: "0 18px", fontSize: asPrintPage ? "9pt" : 11 * scale, letterSpacing: "0.32em", fontWeight: 700, color: INK }}>
            THE CLL COMMITMENT
          </div>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: asPrintPage ? 10 : 14 * scale }}>
          {DEMO.commitment.map(({ icon: Icon, title, line1, line2 }) => (
            <div key={title} style={{ textAlign: "center", padding: asPrintPage ? "8px 4px" : `${10 * scale}px ${6 * scale}px` }}>
              <Icon size={asPrintPage ? 22 : 26 * scale} color={GOLD_DEEP} strokeWidth={1.4} style={{ margin: "0 auto" }} />
              <div style={{ marginTop: 10, fontSize: asPrintPage ? "8.5pt" : 10 * scale, letterSpacing: "0.16em", fontWeight: 700, color: INK }}>
                {title.toUpperCase()}
              </div>
              <div style={{ marginTop: 6, fontSize: asPrintPage ? "8pt" : 9.5 * scale, color: "#555", fontWeight: 300, lineHeight: 1.45 }}>
                {line1}
                <br />
                {line2}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div style={{ height: 1, background: "#e6e6e6", margin: asPrintPage ? "22px 0 18px" : `${26 * scale}px 0 ${22 * scale}px` }} />

      {/* SIGNATURES + TAGLINE BOX */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr auto", gap: asPrintPage ? 22 : 26 * scale, alignItems: "flex-start" }}>
        <div>
          <div style={{ fontSize: asPrintPage ? "8.5pt" : 10 * scale, letterSpacing: "0.25em", fontWeight: 700, color: INK }}>
            PROPOSED BY
          </div>
          <div
            style={{
              marginTop: 14,
              fontFamily: '"Brush Script MT", "Lucida Handwriting", cursive',
              fontSize: asPrintPage ? "20pt" : 24 * scale,
              color: "#222",
              borderBottom: "1px solid #ccc",
              paddingBottom: 4,
              minHeight: asPrintPage ? 30 : 36 * scale,
            }}
          >
            {DEMO.signer.name}
          </div>
          <div style={{ marginTop: 8, fontSize: asPrintPage ? "9pt" : 10.5 * scale, fontWeight: 600 }}>{DEMO.signer.name}</div>
          <div style={{ fontSize: asPrintPage ? "8.5pt" : 10 * scale, color: "#555" }}>{DEMO.signer.title}</div>
          <div style={{ fontSize: asPrintPage ? "8.5pt" : 10 * scale, color: "#555" }}>{DEMO.signer.company}</div>
        </div>

        <div>
          <div style={{ fontSize: asPrintPage ? "8.5pt" : 10 * scale, letterSpacing: "0.25em", fontWeight: 700, color: INK }}>
            APPROVED BY
          </div>
          <div style={{ marginTop: 14, borderBottom: "1px solid #ccc", height: asPrintPage ? 30 : 36 * scale }} />
          <div style={{ marginTop: 8, fontSize: asPrintPage ? "9pt" : 10.5 * scale, color: "#555" }}>Name</div>
          <div style={{ fontSize: asPrintPage ? "9pt" : 10.5 * scale, color: "#555" }}>Title</div>
          <div style={{ fontSize: asPrintPage ? "9pt" : 10.5 * scale, color: "#555" }}>Date</div>
        </div>

        <div
          style={{
            border: `1.5px solid ${GOLD}`,
            padding: asPrintPage ? "16px 18px" : `${18 * scale}px ${22 * scale}px`,
            textAlign: "center",
            minWidth: asPrintPage ? "1.6in" : 170 * scale,
          }}
        >
          <div style={{ fontSize: asPrintPage ? "10pt" : 12 * scale, fontWeight: 700, letterSpacing: "0.16em", color: INK, lineHeight: 1.5 }}>
            ONE CALL.
            <br />
            ONE TEAM.
            <br />
            <span style={{ color: GOLD_DEEP }}>PROJECT COVERED.</span>
          </div>
          <div style={{ marginTop: 12, display: "flex", justifyContent: "center" }}>
            <CllHexMark size={asPrintPage ? 36 : 42 * scale} color={GOLD_DEEP} />
          </div>
        </div>
      </div>

      <div style={{ height: 1, background: "#e6e6e6", margin: asPrintPage ? "18px 0 12px" : `${22 * scale}px 0 ${14 * scale}px` }} />

      {/* FOOTER */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: asPrintPage ? "8pt" : 9.5 * scale, color: "#444", flexWrap: "wrap", gap: 12 }}>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <Phone size={asPrintPage ? 11 : 13} color={GOLD_DEEP} /> {DEMO.contact.phone}
        </span>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <Mail size={asPrintPage ? 11 : 13} color={GOLD_DEEP} /> {DEMO.contact.email}
        </span>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <Globe size={asPrintPage ? 11 : 13} color={GOLD_DEEP} /> {DEMO.contact.web}
        </span>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <MapPin size={asPrintPage ? 11 : 13} color={GOLD_DEEP} /> {DEMO.contact.city}
        </span>
      </div>
    </div>
  );
};

/* ──────────────────────────────────────────────────────────────
   FOLDER VIEW — dark presentation folder + single-page proposal
   (mirrors the ChatGPT mockup)
   ────────────────────────────────────────────────────────────── */
function FolderView() {
  return (
    <div
      data-testid="proposal-sandbox-folder"
      style={{
        minHeight: "100vh",
        background: "#0a0a0a",
        padding: "48px 24px 80px",
      }}
    >
      <div
        style={{
          maxWidth: 1500,
          margin: "0 auto",
          display: "grid",
          gridTemplateColumns: "minmax(360px, 540px) minmax(560px, 760px)",
          gap: 40,
          alignItems: "flex-start",
        }}
      >
        {/* DARK FOLDER (cover) */}
        <div
          style={{
            position: "relative",
            background: "linear-gradient(160deg, #1a1a1a 0%, #050505 100%)",
            border: `1px solid #1f1f1f`,
            borderRadius: 4,
            minHeight: 880,
            padding: "60px 56px",
            color: "#fff",
            overflow: "hidden",
            boxShadow: "0 22px 60px rgba(0,0,0,0.6)",
          }}
        >
          <div
            style={{
              position: "absolute",
              top: 28,
              left: 28,
              right: 28,
              bottom: 28,
              border: `1px solid ${GOLD_DEEP}`,
              opacity: 0.35,
              pointerEvents: "none",
            }}
          />
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: 760, textAlign: "center" }}>
            <CllWordmark height={64} dark />
            <div style={{ marginTop: 28, fontSize: 11, letterSpacing: "0.45em", color: GOLD, fontWeight: 600 }}>
              PRECISION · COMMUNICATION · DELIVERY
            </div>
            <div style={{ width: 60, height: 1, background: GOLD, margin: "180px auto 30px" }} />
            <div style={{ fontSize: 18, letterSpacing: "0.42em", color: "#fff", fontWeight: 600 }}>
              PROJECT COVERED.
            </div>
            <div style={{ fontSize: 18, letterSpacing: "0.42em", color: GOLD, fontWeight: 600, marginTop: 8 }}>
              CONFIDENCE DELIVERED.
            </div>
          </div>
          {/* skyline silhouette */}
          <svg
            viewBox="0 0 800 260"
            preserveAspectRatio="xMidYEnd meet"
            style={{ position: "absolute", left: 0, right: 0, bottom: 0, width: "100%", height: 220, opacity: 0.55 }}
          >
            <defs>
              <linearGradient id="cityGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#1a1a1a" stopOpacity="0" />
                <stop offset="100%" stopColor="#000" stopOpacity="1" />
              </linearGradient>
            </defs>
            <rect x="0" y="0" width="800" height="260" fill="url(#cityGrad)" />
            <path
              fill="#000"
              d="M0,260 L0,200 L40,200 L40,170 L80,170 L80,140 L110,140 L110,100 L140,100 L140,60 L170,60 L170,40 L190,40 L190,80 L220,80 L220,120 L250,120 L250,90 L280,90 L280,150 L310,150 L310,110 L340,110 L340,70 L370,70 L370,30 L400,30 L400,90 L430,90 L430,140 L460,140 L460,100 L490,100 L490,160 L520,160 L520,120 L560,120 L560,80 L590,80 L590,150 L620,150 L620,180 L660,180 L660,140 L700,140 L700,180 L740,180 L740,200 L800,200 L800,260 Z"
            />
          </svg>
        </div>

        {/* SINGLE-PAGE PROPOSAL (offset slightly right, casting shadow) */}
        <div style={{ transform: "translateY(40px) rotate(-0.4deg)" }}>
          <SinglePageProposal scale={1} />
        </div>
      </div>

      <div style={{ textAlign: "center", marginTop: 48, color: "#666", fontSize: 11, letterSpacing: "0.3em", textTransform: "uppercase" }}>
        Mockup view · for stakeholder review
      </div>
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────
   PRINT / PDF VIEW — single letter page, browser-print ready
   ────────────────────────────────────────────────────────────── */
function PrintView() {
  return (
    <div className="py-10 bg-neutral-200 min-h-screen" data-testid="proposal-sandbox-print">
      <style>{`
        @media print {
          @page { size: letter; margin: 0; }
          body { background: #fff !important; }
          .pdf-page { box-shadow: none !important; margin: 0 !important; }
          .no-print { display: none !important; }
        }
      `}</style>
      <div className="pdf-page">
        <SinglePageProposal asPrintPage />
      </div>
      <div className="no-print text-center mt-6">
        <button
          onClick={() => window.print()}
          data-testid="print-button"
          className="px-6 py-3 bg-neutral-900 text-white text-xs tracking-[0.35em] uppercase hover:bg-black inline-flex items-center gap-2"
        >
          <Printer size={14} /> Print / Save as PDF
        </button>
      </div>
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────
   SCREEN VIEW — cinematic landing (kept as the "presentation" mode)
   ────────────────────────────────────────────────────────────── */
const SectionNum = ({ n, title, kicker }) => (
  <div className="flex items-baseline gap-6 mb-8">
    <span className="text-[64px] leading-none font-serif tracking-tight" style={{ color: GOLD }}>{n}</span>
    <div>
      {kicker && (
        <div className="text-[10px] tracking-[0.35em] uppercase mb-1" style={{ color: GOLD }}>
          {kicker}
        </div>
      )}
      <h2 className="text-2xl font-serif font-light text-neutral-900">{title}</h2>
    </div>
  </div>
);

function ScreenView() {
  return (
    <div className="font-sans text-neutral-100" data-testid="proposal-sandbox-screen" style={{ fontFamily: '"Inter", system-ui, sans-serif' }}>
      {/* HERO */}
      <section className="relative overflow-hidden bg-[#0a0a0a] min-h-[92vh] flex items-end">
        <svg viewBox="0 0 1600 240" preserveAspectRatio="none" className="absolute inset-x-0 bottom-0 w-full h-72 opacity-25">
          <path fill="#000" d="M0,240 L0,180 L60,180 L60,150 L110,150 L110,170 L160,170 L160,120 L210,120 L210,90 L240,90 L240,140 L300,140 L300,170 L350,170 L350,110 L400,110 L400,70 L430,70 L430,130 L480,130 L480,160 L540,160 L540,100 L600,100 L600,60 L650,60 L650,140 L710,140 L710,170 L780,170 L780,90 L830,90 L830,40 L870,40 L870,130 L930,130 L930,160 L1000,160 L1000,110 L1050,110 L1050,70 L1100,70 L1100,150 L1160,150 L1160,180 L1240,180 L1240,120 L1290,120 L1290,80 L1340,80 L1340,170 L1410,170 L1410,150 L1470,150 L1470,180 L1540,180 L1540,200 L1600,200 L1600,240 Z" />
        </svg>
        <div className="absolute -right-24 top-1/3 opacity-[0.06] pointer-events-none">
          <EmbossedSeal size={620} />
        </div>
        <div className="absolute left-16 top-16 h-px w-24" style={{ background: GOLD }} />
        <div className="relative z-10 px-16 pb-20 max-w-5xl">
          <CllWordmark height={44} dark />
          <div className="mt-16 text-[11px] tracking-[0.45em] uppercase" style={{ color: GOLD }}>
            Proposal · {DEMO.proposalNumber}
          </div>
          <h1 className="font-serif font-extralight leading-[0.95] tracking-tight text-white mt-6" style={{ fontSize: "clamp(48px, 7vw, 104px)" }}>
            Project Covered.<br />
            <span style={{ color: GOLD }}>Confidence Delivered.</span>
          </h1>
          <p className="mt-10 text-lg text-neutral-400 max-w-xl font-light tracking-wide">
            Precision. Communication. Delivery. — A turn-key subsurface investigation tailored for {DEMO.client}.
          </p>
          <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-10 max-w-3xl">
            {[
              ["Client", DEMO.client],
              ["Project", DEMO.project],
              ["Issued", DEMO.issueDate],
              ["Valid", DEMO.validFor],
            ].map(([k, v]) => (
              <div key={k}>
                <div className="text-[10px] tracking-[0.3em] uppercase mb-2" style={{ color: GOLD }}>{k}</div>
                <div className="text-white text-sm font-light">{v}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SCOPE detail */}
      <section className="bg-white px-16 py-32 text-neutral-900">
        <div className="max-w-5xl mx-auto">
          <SectionNum n="01" kicker="Scope of Services" title="Methods deployed." />
          <div className="grid md:grid-cols-2 gap-x-16 gap-y-10 mt-12">
            {DEMO.scopeDetail.map((s, i) => (
              <div key={s.title} className="border-t pt-6" style={{ borderColor: "#e5e5e5" }}>
                <div className="flex items-start gap-4">
                  <span className="font-serif text-sm tabular-nums mt-1" style={{ color: GOLD }}>
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <div>
                    <h3 className="text-lg font-light text-neutral-900 mb-2">{s.title}</h3>
                    <p className="text-sm font-light leading-relaxed text-neutral-600">{s.detail}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY CLL — uses commitment data */}
      <section className="bg-[#0a0a0a] px-16 py-32 text-white">
        <div className="max-w-5xl mx-auto">
          <SectionNum n="02" kicker="The CLL Commitment" title="Five disciplines. One outcome." />
          <div className="grid md:grid-cols-5 gap-6 mt-12">
            {DEMO.commitment.map(({ icon: Icon, title, line1, line2 }) => (
              <div key={title} className="border border-neutral-800 p-8 hover:border-[#B89B67] transition-colors">
                <Icon size={28} style={{ color: GOLD }} strokeWidth={1.2} />
                <div className="mt-6 text-sm tracking-[0.2em] uppercase">{title}</div>
                <div className="mt-3 text-xs text-neutral-400 font-light leading-relaxed">
                  {line1}<br />{line2}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* INVESTMENT */}
      <section className="bg-white px-16 py-32 text-neutral-900">
        <div className="max-w-5xl mx-auto">
          <SectionNum n="03" kicker="Investment" title="Transparent. Itemized. Honored." />
          <table className="w-full mt-12 text-sm">
            <tbody>
              {DEMO.investment.map((row) => (
                <tr key={row.label} className="border-t border-neutral-200">
                  <td className="py-5 font-light text-neutral-700">{row.label}</td>
                  <td className="py-5 text-right tabular-nums">{fmt(row.amount)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t-2" style={{ borderColor: INK }}>
                <td className="pt-6 text-[10px] tracking-[0.35em] uppercase" style={{ color: GOLD }}>
                  Total Investment
                </td>
                <td className="pt-6 text-right font-serif text-3xl tabular-nums">{fmt(total)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </section>

      {/* CLOSE */}
      <section className="bg-[#0a0a0a] px-16 py-32 text-white text-center relative overflow-hidden">
        <div className="absolute inset-0 flex items-center justify-center opacity-[0.05] pointer-events-none">
          <EmbossedSeal size={520} />
        </div>
        <div className="relative max-w-3xl mx-auto">
          <div className="text-[10px] tracking-[0.5em] uppercase mb-8" style={{ color: GOLD }}>
            Authorization
          </div>
          <h2 className="font-serif font-extralight leading-tight text-4xl md:text-5xl">
            One Call. One Team.
            <br />
            <span style={{ color: GOLD }}>Project Covered.</span>
          </h2>
        </div>
      </section>

      <footer className="bg-[#0a0a0a] text-neutral-400 px-16 py-12 text-xs tracking-[0.18em] uppercase flex items-center justify-between flex-wrap gap-6">
        <div className="flex items-center gap-4">
          <CllHexMark size={32} color={GOLD} />
          <div>
            <div className="text-white">Center Line Locating</div>
            <div>{DEMO.contact.city}</div>
          </div>
        </div>
        <div className="text-right space-y-1">
          <div>{DEMO.contact.web}</div>
          <div>{DEMO.contact.phone}</div>
          <div className="lowercase tracking-wider">{DEMO.contact.email}</div>
        </div>
      </footer>
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────
   ROOT — three modes: Folder (mockup), Screen, Print
   ────────────────────────────────────────────────────────────── */
export default function ProposalSandbox() {
  const [mode, setMode] = useState("folder");

  const tabBtn = (id, label, Icon) => (
    <button
      onClick={() => setMode(id)}
      data-testid={`toggle-${id}`}
      className={`px-4 py-1.5 text-[10px] tracking-[0.25em] uppercase rounded-full transition-colors inline-flex items-center gap-2 ${
        mode === id ? "bg-white text-neutral-900" : "text-neutral-400 hover:text-white"
      }`}
    >
      <Icon size={12} /> {label}
    </button>
  );

  return (
    <div className="min-h-screen bg-neutral-950">
      <div
        className="no-print sticky top-0 z-50 bg-neutral-900/95 backdrop-blur border-b border-neutral-800 text-neutral-200"
        data-testid="sandbox-toolbar"
      >
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <FileText size={16} style={{ color: GOLD }} />
            <span className="text-xs tracking-[0.3em] uppercase">
              Proposal Sandbox · Experimental
            </span>
          </div>
          <div className="flex items-center gap-1 border border-neutral-700 rounded-full p-1">
            {tabBtn("folder", "Folder", FolderOpen)}
            {tabBtn("screen", "Screen", Monitor)}
            {tabBtn("print", "Print / PDF", Printer)}
          </div>
        </div>
      </div>

      {mode === "folder" && <FolderView />}
      {mode === "screen" && <ScreenView />}
      {mode === "print" && <PrintView />}
    </div>
  );
}
